import React, { useEffect, useRef, useState } from "react";
import { ITkSearchFormProps } from "../../../type/componentsType/TkSearchForm";
import { Box } from "@mui/system";
import { Controller, useForm } from "react-hook-form";
import TkSearchFormItem from "../../atoms/TkSearchFormItem";
import TkButton from "../../atoms/TkButton";
import "./_style.scss";

/**
 * TkSearchFormコンポーネントは、検索フォームを表示するためのコンポーネントです。
 *
 * @param {Object} props - TkSearchFormコンポーネントのプロパティ
 * @param {Function} props.onSearch - 検索ボタンがクリックされた時に実行されるコールバック関数
 * @param {Array} props.listData - 検索フォームの項目データの配列
 * @param {Object} props.ruleSchema - 検索フォームのバリデーションルールのスキーマ
 * @param {string} [props.buttonText="検索"] - 検索ボタンのテキスト
 * @param {string} props.className - TkSearchFormコンポーネントのクラス名
 * @param {Array} props.submitButtonGroup - 追加の送信ボタングループの配列
 * @returns {JSX.Element} TkSearchFormコンポーネントのJSX要素
 */
const TkSearchForm: React.FC<ITkSearchFormProps> = (props) => {
  const {
    onSearch,
    getChangeValue,
    listData,
    ruleSchema,
    buttonText = "検索",
    className,
    submitButtonGroup,
  } = props;

  const [errorMsgList, setErrorMsgList] = useState<any>({});
  const [temSaveFlagRef, setTemSaveFlagRef] = useState<any>(false); // 一時保存フラグ
  const submitSource = useRef(0); // 提出元

  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    clearErrors,
  } = useForm<any>({
    mode: "all",
    reValidateMode: "onChange",
  });

  const preventSearch = (
    e: any,
    temSave?: boolean,
    needSubmit?: boolean,
    onClick?: any
  ) => {
    if (needSubmit === false) {
      onClick && onClick();
      return;
    }
    if (temSave) {
      clearErrors();
      goToSearch(getValues(), temSave);

      return;
    }

    handleSubmit((data) => goToSearch(data, temSave))(e);
  };

  const goToSearch = (data: any, temSave?: boolean) => {
    Object.entries(data)?.forEach(([key, value]) => {
      if (!value) delete data[key];
    });
    onSearch && onSearch(data, submitSource.current, temSave);
  };

  const handleChangeValue = (index: number) => (value: string) => {
    getChangeValue && getChangeValue(value, index);
  };

  useEffect(() => {
    setErrorMsgList(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(errors)]);

  useEffect(() => {
    temSaveFlagRef && clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temSaveFlagRef, submitSource]);

  return (
    <Box className={className}>
      <form
        data-testid="tkSearchFormForTest"
        className="tkSearchForm"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        {listData?.map((item, index) => {
          return (
            <Controller
              key={index}
              name={item.keyword}
              control={control}
              rules={ruleSchema && ruleSchema[item.keyword]}
              render={({ field }) => (
                <TkSearchFormItem
                  errorMessage={
                    errorMsgList[item.keyword] &&
                    errorMsgList[item.keyword].message
                  }
                  getChangeValue={handleChangeValue(index)}
                  key={index}
                  {...item}
                  {...field}
                />
              )}
            ></Controller>
          );
        })}

        {!submitButtonGroup && (
          <TkButton
            type="button"
            onClick={() => {
              preventSearch(submitSource.current, false, true);
            }}
          >
            {buttonText}
          </TkButton>
        )}

        {submitButtonGroup?.length &&
          submitButtonGroup.map((item, index) => {
            const { needSubmit = true, temSave = false, onClick } = item;
            if (item.showButton === false)
              return <React.Fragment key={index} />;

            const { submitButtonText, submitButtonDisabled } = item;
            return (
              <TkButton
                key={index}
                ml={!!index}
                onClick={() => {
                  submitSource.current = index;
                  setTemSaveFlagRef(temSave);
                  preventSearch(
                    submitSource.current,
                    temSave,
                    needSubmit,
                    onClick
                  );
                }}
                type="button"
                disabled={submitButtonDisabled}
              >
                {submitButtonText}
              </TkButton>
            );
          })}
      </form>
    </Box>
  );
};

export default TkSearchForm;
